// frontend/modules/template/components/TemplateForm.tsx
import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Modal, Tabs, Alert } from 'antd';
import { useTemplates } from '../hooks/useTemplates';
import { Languages, NotificationPlatforms, TemplateContentType, MultiLanguageTemplateDTO } from '@/shared/api/model';
import { useTheme } from '@/components/common/ThemeProvider';
import { useTemplateStore } from '../stores/template-store';
import { useCreateMultiLanguage, useUpdateMultiLanguage } from '@/shared/api/client/text-template-controller';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

interface TemplateFormProps {
    visible: boolean;
    onCancel: () => void;
}

const TemplateForm: React.FC<TemplateFormProps> = ({ visible, onCancel }) => {
    const [form] = Form.useForm();
    const { dark } = useTheme();

    // Get store state directly
    const {
        activeTemplateName,
        activePlatform,
        activeContentType,
        activeContents,
        activeSubjects,
        isEditMode,
        setActiveContent,
        setActiveSubject,
        resetActiveTemplate
    } = useTemplateStore();

    // Get actions from useTemplates hook
    const {
        isLoading,
        loadTemplates
    } = useTemplates();

    // Local state for form submission
    const [submitting, setSubmitting] = useState(false);

    // Get mutations directly
    const { mutateAsync: createTemplate, isPending: isCreating } = useCreateMultiLanguage();
    const { mutateAsync: updateTemplate, isPending: isUpdating } = useUpdateMultiLanguage();

    // Reset form when modal visibility changes
    useEffect(() => {
        if (visible) {
            form.setFieldsValue({
                name: activeTemplateName || '',
                platform: activePlatform || undefined,
                contentType: activeContentType || undefined
            });
        } else {
            form.resetFields();
        }
    }, [visible, form, activeTemplateName, activePlatform, activeContentType]);

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    const handleOk = async () => {
        try {
            // Validate form fields
            const values = await form.validateFields();

            // Get form and store values directly before submission
            const name = values.name;
            const platform = values.platform;
            const contentType = values.contentType;
            const contents = { ...activeContents };
            const subjects = { ...activeSubjects };

            console.log("Form values at submission time:", { name, platform, contentType, contents, subjects });

            // Validate content values
            if (!contents[Languages.EN] || !contents[Languages.SW]) {
                showErrorNotification('Error', 'Content is required for both English and Swahili', dark);
                return;
            }

            // Validate subject values for EMAIL and WHATS_APP platforms
            if ((platform === NotificationPlatforms.EMAIL || platform === NotificationPlatforms.WHATS_APP) && (!subjects[Languages.EN] || !subjects[Languages.SW])) {
                showErrorNotification('Error', 'Subject is required for both English and Swahili', dark);
                return;
            }

            // Start submission
            setSubmitting(true);

            try {
                const templateData: MultiLanguageTemplateDTO = {
                    name,
                    platform,
                    contentType,
                    contentByLanguage: contents,
                    subjectByLanguage: subjects
                };

                if (isEditMode) {
                    // Update existing template
                    await updateTemplate({
                        name,
                        platform,
                        data: templateData
                    });
                    showSuccessNotification('Success', 'Template updated successfully', dark);
                } else {
                    // Create new template
                    await createTemplate({ data: templateData });
                    showSuccessNotification('Success', 'Template created successfully', dark);
                }

                // Reload templates and close modal
                await loadTemplates();
                resetActiveTemplate();
                handleCancel();
            } catch (err) {
                console.error('Error saving template:', err);
                showErrorNotification('Error', 'Failed to save template', dark);
            } finally {
                setSubmitting(false);
            }
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const handleContentChange = (language: Languages, content: string) => {
        setActiveContent(language, content);
    };

    const handleSubjectChange = (language: Languages, subject: string) => {
        setActiveSubject(language, subject);
    };

    return (
        <Modal
            title={isEditMode ? 'Edit Template' : 'Create Template'}
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={isLoading || submitting || isCreating || isUpdating}
            width={800}
            className={dark ? 'dark' : ''}
        >
            <Form
                form={form}
                layout="vertical"
                className="mt-4"
            >
                <Form.Item
                    name="name"
                    label="Template Name"
                    rules={[
                        { required: true, message: 'Please enter template name' },
                        {
                            pattern: /^[A-Z0-9_]+$/,
                            message: 'Template name must contain only uppercase letters, numbers, and underscores'
                        },
                        {
                            min: 3,
                            message: 'Template name must be at least 3 characters long'
                        }
                    ]}
                >
                    <Input
                        placeholder="e.g., LOAN_APPLICATION_CONFIRMATION"
                        disabled={isEditMode}
                        style={{ textTransform: 'uppercase' }}
                        onChange={(e) => {
                            // Auto-convert to uppercase
                            const value = e.target.value.toUpperCase();
                            form.setFieldValue('name', value);
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="platform"
                    label="Platform"
                    rules={[{ required: true, message: 'Please select platform' }]}
                >
                    <Select
                        placeholder="Select platform"
                        disabled={isEditMode}
                        onChange={(value) => {
                            // Update the active platform state to make subject fields reactive
                            const { setActiveTemplate } = useTemplateStore.getState();
                            setActiveTemplate(activeTemplateName, value, activeContentType);
                        }}
                    >
                        <Option value={NotificationPlatforms.WHATS_APP}>WhatsApp</Option>
                        <Option value={NotificationPlatforms.SMS}>SMS</Option>
                        <Option value={NotificationPlatforms.EMAIL}>Email</Option>
                        <Option value={NotificationPlatforms.IN_APP}>In-App</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="contentType"
                    label="Content Type"
                    rules={[{ required: true, message: 'Please select content type' }]}
                >
                    <Select
                        placeholder="Select content type"
                        disabled={isEditMode}
                    >
                        <Option value={TemplateContentType.PLAIN}>Plain Text</Option>
                        <Option value={TemplateContentType.HTML}>HTML</Option>
                    </Select>
                </Form.Item>

                <Alert
                    className="mb-4"
                    message="Template Variables Example"
                    description={
                        <div className="text-sm">
                            <p>Use double curly braces for variables: <code>&#123;&#123;variableName&#125;&#125;</code></p>
                            <p>Example: <code>Hello &#123;&#123;customerName&#125;&#125;, your balance is &#123;&#123;balance&#125;&#125;</code></p>
                            {(activePlatform === NotificationPlatforms.EMAIL || activePlatform === NotificationPlatforms.WHATS_APP) && (
                                <p><strong>Note:</strong> Subject fields are available for Email and WhatsApp platforms.</p>
                            )}
                            {activePlatform === NotificationPlatforms.IN_APP && (
                                <div>
                                    <p><strong>In-App Templates:</strong> Support rich JSON content with action buttons and metadata.</p>
                                    <p>Additional variables: <code>&#123;&#123;priority&#125;&#125;</code>, <code>&#123;&#123;category&#125;&#125;</code>, <code>&#123;&#123;actionLink&#125;&#125;</code></p>
                                </div>
                            )}
                        </div>
                    }
                    type="info"
                    showIcon
                />

                <Tabs defaultActiveKey="en" className="mb-4">
                    <TabPane tab="English" key="en">
                        <Form.Item
                            label="English Content"
                            required
                            rules={[{ required: true, message: 'English content is required' }]}
                        >
                            <TextArea
                                rows={6}
                                value={activeContents[Languages.EN]}
                                onChange={(e) => handleContentChange(Languages.EN, e.target.value)}
                                className={dark ? 'bg-gray-800 text-white border-gray-700' : ''}
                                placeholder="Enter English template content"
                            />
                        </Form.Item>
                        {activePlatform === NotificationPlatforms.EMAIL || activePlatform === NotificationPlatforms.WHATS_APP ? (
                            <Form.Item
                                label="English Subject"
                                required
                                rules={[{ required: true, message: 'English subject is required' }]}
                            >
                                <Input
                                    value={activeSubjects[Languages.EN]}
                                    onChange={(e) => handleSubjectChange(Languages.EN, e.target.value)}
                                    className={dark ? 'bg-gray-800 text-white border-gray-700' : ''}
                                    placeholder="Enter English template subject"
                                />
                            </Form.Item>
                        ) : null}
                    </TabPane>

                    <TabPane tab="Swahili" key="sw">
                        <Form.Item
                            label="Swahili Content"
                            required
                            rules={[{ required: true, message: 'Swahili content is required' }]}
                        >
                            <TextArea
                                rows={6}
                                value={activeContents[Languages.SW]}
                                onChange={(e) => handleContentChange(Languages.SW, e.target.value)}
                                className={dark ? 'bg-gray-800 text-white border-gray-700' : ''}
                                placeholder="Enter Swahili template content"
                            />
                        </Form.Item>
                        {activePlatform === NotificationPlatforms.EMAIL || activePlatform === NotificationPlatforms.WHATS_APP ? (
                            <Form.Item
                                label="Swahili Subject"
                                required
                                rules={[{ required: true, message: 'Swahili subject is required' }]}
                            >
                                <Input
                                    value={activeSubjects[Languages.SW]}
                                    onChange={(e) => handleSubjectChange(Languages.SW, e.target.value)}
                                    className={dark ? 'bg-gray-800 text-white border-gray-700' : ''}
                                    placeholder="Enter Swahili template subject"
                                />
                            </Form.Item>
                        ) : null}
                    </TabPane>
                </Tabs>
            </Form>
        </Modal>
    );
};

export default TemplateForm;